/**
 * Catálogo canónico de rutas del frontend (única fuente de verdad).
 *
 * **Por qué este módulo existe:** los hardcoded path strings repartidos por
 * el código (en `<Link to="/foo/${id}">` y `navigate("/foo")` ) son una de
 * las fuentes de bugs más insidiosas en React: el TypeScript no valida que
 * la URL corresponda a una ruta registrada, ESLint tampoco, los tests
 * tampoco. El único validador histórico fue "un usuario clickea y ve
 * página en blanco" (caso real: ver historia de Git, fix RT26).
 *
 * Reglas de uso:
 *  - **El router** (`router.tsx`) consume `paths.ROUTE_*` para registrar
 *    `<Route path={...}>`. Si la URL pattern cambia, se cambia acá y el
 *    router queda sincronizado automáticamente.
 *  - **Los callers** (componentes con `Link`/`navigate`) usan los builders
 *    `paths.expedienteDetalle(id)`, `paths.proyectoLatex(id)`, etc. Cada
 *    builder recibe argumentos tipados y devuelve la URL formada.
 *  - **NUNCA escribas un string literal de URL** en otro archivo. Si lo
 *    tenés que escribir, primero agregalo acá.
 *
 * Convención del nombre:
 *  - `ROUTE_X` (mayúsculas) = patrón de ruta para el router (`/foo/:id`).
 *  - `paths.x(id)` (camelCase) = builder que devuelve la URL formada (`/foo/123`).
 *
 * Tests: ver `paths.spec.ts`. Cualquier ruta nueva del router debe agregarse
 * acá y tener su builder + test antes de usarse en un componente.
 */

// ───────────────────────────────────────────────────────────────────────────
// Patterns de ruta (lo que recibe `<Route path={...}>`)
// ───────────────────────────────────────────────────────────────────────────

export const ROUTE_LOGIN = "/login" as const;
export const ROUTE_VERIFICAR_QR = "/verificar/:anchor" as const;
// Activación de cuenta por invitación (ADR 053). Pública y sin AppShell: el
// invitado aún no tiene sesión. El token viaja en el path.
export const ROUTE_ACTIVAR = "/activar/:token" as const;

// Landing público (ADR 051). Vive en la raíz: un visitante sin sesión ve la
// landing; un usuario autenticado es redirigido a su dashboard (`/inicio`).
export const ROUTE_HOME = "/" as const;
// El dashboard autenticado se movió de "/" a "/inicio" al introducir la landing
// pública (ADR 051). `paths.dashboard()` sigue siendo la fuente única, así que
// los callers (login, "volver al panel", catch-all) no cambian de string.
export const ROUTE_DASHBOARD = "/inicio" as const;
export const ROUTE_PERFIL = "/perfil" as const;

// Expedientes — el rol estudiante ve los propios; el resto los autorizados.
// El estudiante crea expedientes vía `/solicitudes/nueva` (wizard ADR 015);
// la ruta legacy `/expedientes/nuevo` fue eliminada (ADR 020).
export const ROUTE_MIS_EXPEDIENTES = "/expedientes" as const;
export const ROUTE_EXPEDIENTE_DETALLE = "/expedientes/:id" as const;
export const ROUTE_EXPEDIENTE_PROYECTO_LATEX = "/expedientes/:id/proyecto" as const;
export const ROUTE_EXPEDIENTE_PROPONER_ASESORIA =
  "/expedientes/:expedienteId/proponer-asesoria" as const;
// ROUTE_EXPEDIENTE_PRESENTAR_DECANATO eliminado (ADR 036): la aceptación del
// asesor presenta la solicitud al Decanato automáticamente.

// Solicitudes (estudiante).
export const ROUTE_MIS_SOLICITUDES = "/solicitudes" as const;
export const ROUTE_SOLICITUD_NUEVA = "/solicitudes/nueva" as const;

// Bandejas por rol.
export const ROUTE_BANDEJA_DECANATO = "/decanato" as const;
export const ROUTE_REPORTES_DECANATO = "/decanato/reportes" as const;
export const ROUTE_BANDEJA_ASESORIA = "/asesoria" as const;
// ADR 020: /asesoria/propuestas fue eliminada. La sección de propuestas
// recibidas se integró dentro de "Mis asesorías" como bloque superior.
export const ROUTE_BANDEJA_JURADO = "/jurado" as const;
// ADR 020: /repositorio fue eliminado. RepositorioDashboard vive en `/`.

// Admin.
export const ROUTE_ADMIN_FERIADOS = "/admin/feriados" as const;
// Estructura académica del tenant (facultades y escuelas/EAPs) — ADR 055.
export const ROUTE_ADMIN_ACADEMICO = "/admin/academico" as const;
// Hub de estudiantes del ADMIN (listado + alta individual + import Excel) — ADR 055.
export const ROUTE_ADMIN_ESTUDIANTES = "/admin/estudiantes" as const;
// Gestión de docentes y sus capacidades (asesor/jurado) — ADR 054.
export const ROUTE_ADMIN_USUARIOS = "/admin/usuarios" as const;
// Plataforma (SUPER_ADMIN) — ADR 045.
export const ROUTE_ADMIN_INSTITUCIONES = "/admin/instituciones" as const;
// Personalización self-service del tenant (ADMIN) — ADR 047.
export const ROUTE_ADMIN_PERSONALIZACION = "/admin/personalizacion" as const;
// Styleguide de componentes — sólo DEV (no se registra en producción).
export const ROUTE_DEV_STYLEGUIDE = "/admin/styleguide" as const;
export const ROUTE_ADMIN_CATCH_ALL = "/admin/*" as const;

// ───────────────────────────────────────────────────────────────────────────
// Builders — usan los patterns y devuelven URLs concretas
// ───────────────────────────────────────────────────────────────────────────

/**
 * URLs sin parámetros — los tipamos como constantes literales para que el
 * autocompletado funcione y un typo se atrape en compile time.
 */
export const paths = {
  // Páginas sin parámetros (mismas que ROUTE_*).
  home: () => ROUTE_HOME,
  login: () => ROUTE_LOGIN,
  dashboard: () => ROUTE_DASHBOARD,
  perfil: () => ROUTE_PERFIL,
  misExpedientes: () => ROUTE_MIS_EXPEDIENTES,
  misSolicitudes: () => ROUTE_MIS_SOLICITUDES,
  solicitudNueva: () => ROUTE_SOLICITUD_NUEVA,
  bandejaDecanato: () => ROUTE_BANDEJA_DECANATO,
  reportesDecanato: () => ROUTE_REPORTES_DECANATO,
  bandejaAsesoria: () => ROUTE_BANDEJA_ASESORIA,
  bandejaJurado: () => ROUTE_BANDEJA_JURADO,
  adminFeriados: () => ROUTE_ADMIN_FERIADOS,
  adminAcademico: () => ROUTE_ADMIN_ACADEMICO,
  adminEstudiantes: () => ROUTE_ADMIN_ESTUDIANTES,
  adminInstituciones: () => ROUTE_ADMIN_INSTITUCIONES,
  adminPersonalizacion: () => ROUTE_ADMIN_PERSONALIZACION,

  // Páginas con parámetros — builders tipados.
  verificarQr: (anchor: string) => `/verificar/${anchor}` as const,
  activar: (token: string) => `/activar/${token}` as const,
  expedienteDetalle: (id: string) => `/expedientes/${id}` as const,
  /**
   * Editor LaTeX del proyecto digital del expediente. El router registra
   * esto bajo `/expedientes/:id/proyecto` (NO `/proyecto-latex/:id` —
   * error histórico documentado en RT26).
   */
  expedienteProyectoLatex: (expedienteId: string) =>
    `/expedientes/${expedienteId}/proyecto` as const,
  expedienteProponerAsesoria: (expedienteId: string) =>
    `/expedientes/${expedienteId}/proponer-asesoria` as const,
} as const;
